import { useState, useRef } from "react";
import { toast } from "sonner";
import { Phone, Camera, X, Loader2, CheckCircle2, Siren, Send } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import Reveal from "../components/Reveal";
import { useSite, phoneDisplay } from "../context/SiteContext";

const URGENCY = ["Immediate", "Today", "Within a few days"];
const EXAMPLES = ["Fire damage", "Storm & wind damage", "Damaged walls, roofs, doors or windows", "Temporary boarding & covering", "Emergency weatherproofing", "Water-related damage", "Impact or accidental damage", "Insurance repair work"];

const inputCls = "w-full border border-line bg-white px-4 py-3 text-sm text-ink placeholder:text-slate2/50 focus:outline-none focus:border-[#B3261E] focus:ring-1 focus:ring-[#B3261E] transition-colors";
const labelCls = "block font-mono text-[11px] tracking-[0.15em] uppercase text-slate2 mb-2";

export default function UrgentSection() {
  const site = useSite();
  const [fields, setFields] = useState({ name: "", phone: "", address: "", what_happened: "", urgency: "", website: "" });
  const [photos, setPhotos] = useState([]);
  const [phase, setPhase] = useState("form");
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const set = (k) => (e) => setFields({ ...fields, [k]: e.target.value });

  const addPhotos = (list) => {
    const next = [...photos];
    for (const f of Array.from(list)) {
      if (!f.type.startsWith("image/") && !/\.(heic|heif)$/i.test(f.name)) { toast.error(`${f.name}: photos only`); continue; }
      if (f.size > 30 * 1024 * 1024) { toast.error(`${f.name} is larger than 30MB`); continue; }
      if (next.length >= 5) { toast.error("Maximum 5 photos"); break; }
      next.push({ file: f, id: Math.random().toString(36).slice(2) });
    }
    setPhotos(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!fields.name.trim() || !fields.phone.trim()) { toast.error("Please enter your name and phone number"); return; }
    if (!fields.urgency) { toast.error("Please select how urgent it is"); return; }
    setPhase("sending");
    setProgress(0);
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
    photos.forEach((p) => fd.append("files", p.file));
    try {
      await api.post("/enquiries/urgent", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => setProgress(Math.round((ev.loaded / (ev.total || 1)) * 100)),
      });
      setPhase("done");
    } catch (err) {
      setPhase("form");
      toast.error(formatApiError(err) || "Sending failed — please call us directly.");
    }
  };

  return (
    <section id="urgent" data-testid="urgent-section" className="py-16 md:py-24 bg-[#16110D] text-white scroll-mt-20 border-y border-[#B3261E]/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <Reveal className="lg:col-span-5">
            <p className="inline-flex items-center gap-2.5 font-mono text-[11px] tracking-[0.25em] uppercase text-[#FF6B5E]">
              <Siren className="w-4 h-4" /> Urgent &amp; Emergency Building Work
            </p>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-5xl tracking-tight display-expanded leading-[1.02]">
              Need Urgent Building Help?
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed">
              If your property has been damaged and needs urgent building work, temporary protection or make-safe repairs, contact Razor Build and tell us what's happened.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {EXAMPLES.map((x) => (
                <span key={x} className="font-mono text-[10px] tracking-[0.12em] uppercase text-white/55 border border-white/15 px-3 py-1.5">{x}</span>
              ))}
            </div>
            <a href={`tel:${site.phone}`} data-testid="urgent-call-btn" className="mt-8 inline-flex items-center gap-3 bg-[#B3261E] hover:bg-[#93201a] text-white font-mono text-xs tracking-[0.18em] uppercase px-7 py-4 transition-colors">
              <Phone className="w-4 h-4" /> Call Razor Build — {phoneDisplay(site.phone)}
            </a>
            <p className="mt-6 text-xs text-white/45 leading-relaxed max-w-md">
              Major project, small repair or unexpected damage — talk to Razor Build and we'll see how we can help.
            </p>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-7">
            {phase === "done" ? (
              <div data-testid="urgent-success" className="bg-white text-ink p-8 md:p-12 text-center h-full flex flex-col items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-[#B3261E] mb-5" />
                <h3 className="font-display font-bold text-2xl mb-3">We've received your urgent enquiry</h3>
                <p className="text-slate2 leading-relaxed max-w-md">
                  Thanks {fields.name.split(" ")[0]} — your details{photos.length > 0 ? " and photos" : ""} have been sent to Razor Build. If the situation is immediate, call us now on <a href={`tel:${site.phone}`} className="text-[#B3261E] font-semibold">{phoneDisplay(site.phone)}</a>.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="urgent-form" className="bg-white text-ink p-6 md:p-8">
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#B3261E] mb-5">Send Urgent Enquiry</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="urg-name" className={labelCls}>Name*</label>
                    <input id="urg-name" data-testid="urgent-name" required value={fields.name} onChange={set("name")} className={inputCls} placeholder="Your name" autoComplete="name" />
                  </div>
                  <div>
                    <label htmlFor="urg-phone" className={labelCls}>Phone Number*</label>
                    <input id="urg-phone" data-testid="urgent-phone" type="tel" required value={fields.phone} onChange={set("phone")} className={inputCls} placeholder="021 000 0000" autoComplete="tel" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="urg-address" className={labelCls}>Property Address / Suburb</label>
                    <input id="urg-address" data-testid="urgent-address" value={fields.address} onChange={set("address")} className={inputCls} placeholder="Where is the damaged property?" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="urg-what" className={labelCls}>What happened?</label>
                    <textarea id="urg-what" data-testid="urgent-what" rows={3} value={fields.what_happened} onChange={set("what_happened")} className={`${inputCls} resize-y`} placeholder="Briefly describe the damage and what you need — e.g. storm took tiles off the roof and water is getting in." />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="urg-urgency" className={labelCls}>How urgent is it?*</label>
                    <select id="urg-urgency" data-testid="urgent-urgency" required value={fields.urgency} onChange={set("urgency")} className={inputCls}>
                      <option value="">Select urgency</option>
                      {URGENCY.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                </div>

                <input type="text" name="website" value={fields.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                <div className="mt-5">
                  <button type="button" onClick={() => inputRef.current?.click()} data-testid="urgent-photo-btn" className="w-full inline-flex items-center justify-center gap-2.5 border-2 border-dashed border-line hover:border-[#B3261E]/50 px-4 py-5 text-sm text-slate2 transition-colors">
                    <Camera className="w-5 h-5 text-[#B3261E]" /> Add photos of the damage (optional)
                  </button>
                  <input ref={inputRef} type="file" multiple accept="image/*,.heic,.heif" className="hidden" data-testid="urgent-photo-input" onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
                  {photos.length > 0 && (
                    <ul data-testid="urgent-photos-list" className="mt-3 space-y-2">
                      {photos.map((p) => (
                        <li key={p.id} className="flex items-center gap-3 text-sm bg-paper border border-line px-3.5 py-2.5">
                          <Camera className="w-4 h-4 text-[#B3261E] shrink-0" />
                          <span className="truncate flex-1">{p.file.name}</span>
                          <button type="button" onClick={() => setPhotos(photos.filter((x) => x.id !== p.id))} data-testid={`urgent-photo-remove-${p.id}`} className="p-1 text-slate2 hover:text-red-600" aria-label={`Remove ${p.file.name}`}>
                            <X className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {phase === "sending" && (
                    <div className="mt-3">
                      <div className="flex justify-between font-mono text-[10px] tracking-wider uppercase text-slate2 mb-1.5">
                        <span>{progress < 100 ? "Uploading…" : "Sending…"}</span><span>{progress}%</span>
                      </div>
                      <div className="h-1 bg-line overflow-hidden"><div className="h-full bg-[#B3261E] transition-[width] duration-300" style={{ width: `${progress}%` }} /></div>
                    </div>
                  )}
                </div>

                <button type="submit" disabled={phase === "sending"} data-testid="urgent-submit-btn" className="mt-5 w-full inline-flex items-center justify-center gap-3 bg-[#B3261E] hover:bg-[#93201a] text-white font-mono text-xs tracking-[0.2em] uppercase px-6 py-4 disabled:opacity-60 transition-colors">
                  {phase === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {phase === "sending" ? "Sending…" : "Send Urgent Enquiry"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
